import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, CalendarDays, Users, UserCog, ClipboardList,
  CreditCard, Package, ChevronLeft, LogOut,
  FileText, Receipt, Star, Crown, Contact, BarChart3, Landmark, FileBarChart,
  Settings, ShieldCheck, Building2, Blocks, Sliders, Megaphone, UserCheck, RefreshCcw, Wallet, Warehouse, PackageCheck,
} from 'lucide-react';

import styles from './Sidebar.module.css';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import logoNavy from '../../assets/hirsta-logo-navy.png';
import markWhite from '../../assets/hirsta-mark-white.png';

// Each item uses the same access key as its page (ProtectedRoute) and the server (access guard):
//   module + action   -> hasPermission(module, action)
//   any: ['m.a', ...] -> any one of them
//   multiModule       -> the action on any of these modules
// badge: small label next to new screens
const salonNavGroups = [
  {
    title: '',
    items: [
      { name: 'Dashboard', path: '/', icon: LayoutDashboard, module: 'dashboard', action: 'view' },
      { name: 'Create Bill', path: '/create-bill', icon: FileText, module: 'billing', action: 'create' },
      { name: 'Booking History', path: '/booking-history', icon: CalendarDays, any: ['appointments.view_booking_history', 'billing.view_history'] },
      { name: 'Invoices', path: '/invoices', icon: Receipt, module: 'billing', action: 'view_history' },
      { name: 'Renew Loyalty', path: '/renew-loyalty', icon: RefreshCcw, multiModule: ['memberships', 'packages', 'billing'], action: 'view' },
      { name: 'Appointments', path: '/appointments', icon: CalendarDays, module: 'appointments', action: 'view' },
    ],
  },
  {
    title: 'MONEY',
    items: [
      { name: 'Daily Report', path: '/reports/daily', icon: FileBarChart, any: ['reports.view_daily_report', 'reports.view_eod_report'], badge: 'New' },
      { name: 'Cash Drawer', path: '/cash-flow', icon: Landmark, module: 'cashflow', action: 'view', badge: 'New' },
      { name: 'Expenses', path: '/expenses', icon: CreditCard, module: 'expense', action: 'view' },
    ],
  },
  {
    title: 'BUSINESS',
    items: [
      { name: 'Analytics & Reports', path: '/analytics', icon: BarChart3, module: 'reports', action: 'view' },
      { name: 'Feedback', path: '/feedback', icon: Star, module: 'feedback', action: 'view' },
      { name: 'Marketing', path: '/marketing', icon: Megaphone, module: 'marketing', action: 'view' },
    ],
  },
  {
    title: 'MANAGE',
    items: [
      { name: 'Clients', path: '/clients', icon: Contact, module: 'customers', action: 'view' },
      { name: 'Pricelist', path: '/pricelist/overview', icon: ClipboardList, module: 'pricelist', action: 'view' },
      { name: 'Inventory', path: '/inventory', icon: Package, module: 'inventory', action: 'view' },
      // stock HQ sent from the warehouse: receive it here
      { name: 'Warehouse stock', path: '/warehouse', icon: PackageCheck, any: ['inventory.view', 'warehouse.receive', 'inventory.edit'] },
      { name: 'Staff', path: '/staff', icon: UserCog, module: 'staff', action: 'view' },
      { name: 'Attendance', path: '/attendance', icon: UserCheck, module: 'attendance', action: 'view' },
      { name: 'Payroll', path: '/payroll', icon: Wallet, module: 'payroll', action: 'view' },
      { name: 'Users', path: '/users', icon: Users, module: 'users', action: 'view' },
      { name: 'Loyalty', path: '/manage-loyalty-program', icon: Crown, multiModule: ['memberships', 'packages'], action: 'view' },
      { name: 'Integrations', path: '/integrations', icon: Blocks, module: 'integrations', action: 'view' },
      { name: 'Config', path: '/config', icon: Sliders, module: 'settings', action: 'view' },
    ],
  },
];

const hqNavGroups = [
  {
    title: 'ENTERPRISE CONTROL',
    items: [
      { name: 'HQ Overview', path: '/', icon: Building2, module: 'dashboard', action: 'view' },
      { name: 'Global Marketing', path: '/marketing', icon: Megaphone, module: 'marketing', action: 'manage_global' },
      { name: 'Global Rules', path: '/settings', icon: Settings, module: 'settings', action: 'manage_global' },
      { name: 'Integrations', path: '/integrations', icon: Blocks, module: 'settings', action: 'manage_global' },
      { name: 'App Config', path: '/config', icon: Sliders, module: 'settings', action: 'manage_global' },
      { name: 'Branch Access Limits', path: '/global-access', icon: ShieldCheck, module: 'settings', action: 'manage_global' },
    ],
  },
  {
    title: 'FRANCHISE',
    items: [
      // royalty access, or whoever runs HQ's global settings (owner / admin); the page and server check the same
      { name: 'Royalty', path: '/royalty', icon: Crown, any: ['royalty.view', 'royalty.manage', 'settings.manage_global'], badge: 'New' },
      { name: 'Warehouse', path: '/warehouse', icon: Warehouse, any: ['warehouse.view', 'warehouse.manage', 'settings.manage_global'], badge: 'New' },
    ],
  },
];

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 1024);
  const location = useLocation();
  const { hasPermission, logout, user } = useAuth();
  const { activeStore } = useStore();

  // collapse on small screens, only when the size class changes (keeps a manual choice otherwise)
  useEffect(() => {
    let small = window.innerWidth <= 1024;
    const onResize = () => {
      const nowSmall = window.innerWidth <= 1024;
      if (nowSmall !== small) { small = nowSmall; setIsCollapsed(nowSmall); }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isHQActive = activeStore?.isHQ || activeStore?.id === 'global_hq';
  const currentNavGroups = isHQActive ? hqNavGroups : salonNavGroups;

  // "/reports/daily" must not also light up "/analytics"; the longest matching path wins
  const bestMatch = currentNavGroups
    .flatMap((g) => g.items.map((i) => i.path))
    .filter((p) => (p === '/' ? location.pathname === '/' : location.pathname === p || location.pathname.startsWith(`${p}/`)))
    .sort((a, b) => b.length - a.length)[0];

  const canSee = (item) => {
    if (user?.role === 'SUPERADMIN') return true;
    if (item.roles) return item.roles.includes(user?.role);
    if (item.any) return item.any.some((key) => { const [m, a] = key.split('.'); return hasPermission(m, a); });
    if (item.multiModule) return item.multiModule.some((mod) => hasPermission(mod, item.action));
    return hasPermission(item.module, item.action);
  };

  return (
    <aside
      className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''} ${isHQActive ? styles.hq : ''}`}
      aria-label="Sidebar"
    >
      {/* brand: full logo and "H" mark sit in the same place and cross-fade */}
      <div className={styles.header}>
        <div className={styles.brand} title="Hirsta">
          <img src={logoNavy} alt="Hirsta" className={styles.brandLogo} />
          <span className={styles.brandMark} aria-hidden="true"><img src={markWhite} alt="" /></span>
          {isHQActive && <span className={styles.hqTag}>HQ</span>}
        </div>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setIsCollapsed((v) => !v)}
          aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          aria-expanded={!isCollapsed}
        >
          <ChevronLeft size={14} strokeWidth={3} />
        </button>
      </div>

      <nav className={styles.nav} aria-label="Main menu">
        {currentNavGroups.map((group) => {
          const allowedItems = group.items.filter(canSee);
          if (allowedItems.length === 0) return null;

          return (
            <div key={group.title || 'main'} className={styles.group}>
              {group.title && (
                <div className={styles.groupLabel}>
                  <span>{group.title}</span>
                  <i aria-hidden="true" />
                </div>
              )}
              {allowedItems.map((item) => {
                const Icon = item.icon;
                const isActive = bestMatch === item.path;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    title={isCollapsed ? item.name : undefined}
                    aria-current={isActive ? 'page' : undefined}
                    className={`${styles.item} ${isActive ? styles.active : ''}`}
                  >
                    <span className={styles.icon}>
                      <Icon size={15} strokeWidth={2.4} />
                      {item.badge && <i className={styles.dot} aria-hidden="true" />}
                    </span>
                    <span className={styles.label}>{item.name}</span>
                    {item.badge && <span className={styles.badge}>{item.badge}</span>}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className={styles.bottom}>
        <button type="button" className={`${styles.item} ${styles.logout}`} onClick={logout} title={isCollapsed ? 'Logout' : undefined}>
          <span className={styles.icon}><LogOut size={15} strokeWidth={2.4} /></span>
          <span className={styles.label}>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
